import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { navigation } from '../config/navigation';
import { buildNavigation } from '../config/navigation';
import {
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
  UserIcon,
  UserGroupIcon,
  DocumentTextIcon,
  AdjustmentsHorizontalIcon
} from '@heroicons/react/24/outline';

const TopNavigation = () => {
  const { checkPermission, hasAnyPermission, logout, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);
  const [adminSettingsDropdownOpen, setAdminSettingsDropdownOpen] = useState(false);
  const [newMenuDropdownOpen, setNewMenuDropdownOpen] = useState(false);
  const [viewSearchDropdownOpen, setViewSearchDropdownOpen] = useState(false);
  const [generatedPages, setGeneratedPages] = useState([]);
  const [generatedPagesDropdownOpen, setGeneratedPagesDropdownOpen] = useState(false);

  // Fetch generated pages for the menu
  useEffect(() => {
    const fetchPages = async () => {
      const nav = await buildNavigation();
      const adminItem = nav.find(item => item.name === 'Admin');
      const adminSettings = adminItem?.children?.find(child => child.name === 'Admin Settings');
      const generatedPagesMenu = adminSettings?.children?.find(child => child.name === 'Other Applications');
      // Flatten all category groups into a single array of pages
      let pages = [];
      if (generatedPagesMenu && Array.isArray(generatedPagesMenu.children)) {
        generatedPagesMenu.children.forEach(categoryGroup => {
          if (categoryGroup.children) {
            pages = pages.concat(categoryGroup.children);
          }
        });
      }
      setGeneratedPages(pages);
    };
    fetchPages();
  }, []);

  const hasAccess = (item) => {
    // Check single permission
    if (item.requiredPermission) {
      return checkPermission(item.requiredPermission);
    }

    // Check multiple permissions (any of them)
    if (item.requiredPermissions && Array.isArray(item.requiredPermissions)) {
      return hasAnyPermission(item.requiredPermissions);
    }

    // If no permission requirement, allow access
    return true;
  };

  const filteredNavigation = navigation.filter(item => hasAccess(item));

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (href) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  // Get Admin submenu items
  const getAdminSubmenuItems = () => {
    const adminItem = filteredNavigation.find(item => item.name === 'Admin');
    return adminItem?.children || [];
  };

  // Get Admin Settings submenu items
  const getAdminSettingsSubmenuItems = () => {
    const adminItem = filteredNavigation.find(item => item.name === 'Admin');
    const adminSettingsItem = adminItem?.children?.find(child => child.name === 'Admin Settings');
    return adminSettingsItem?.children || [];
  };

  const adminSubmenuItems = getAdminSubmenuItems();
  const adminSettingsSubmenuItems = getAdminSettingsSubmenuItems();

  // Filter items based on permissions
  const filteredAdminSubmenuItems = adminSubmenuItems.filter(item =>
    item.requiredPermission ? checkPermission(item.requiredPermission) : true
  );

  const filteredAdminSettingsSubmenuItems = adminSettingsSubmenuItems.filter(item =>
    item.requiredPermission ? checkPermission(item.requiredPermission) : true
  );

  return (
    <nav className="bg-white shadow-soft border-b border-gray-200 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo and Desktop Navigation */}
          <div className="flex items-center">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center">
              <Link to="/">
                <img
                  src={process.env.PUBLIC_URL + '/logo.svg'}
                  alt="Data Manager Logo"
                  className="h-10 w-auto"
                  style={{ maxHeight: '40px' }}
                  draggable="false"
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:ml-6 md:flex md:space-x-8">
              {filteredNavigation.map((item) => {
                // Special handling for New menu with dropdown
                if (item.name === 'New' && item.children && item.children.length > 0) {
                  return (
                    <div key={item.href} className="relative">
                      <button style={{ height: '100%' }}
                        onClick={() => setNewMenuDropdownOpen(!newMenuDropdownOpen)}
                        className={`${isActive(item.href)
                            ? 'border-indigo-500 text-gray-900'
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                          } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-all duration-200 hover:shadow-sm`}
                      >
                        {item.iconComponent ? (
                          <item.iconComponent
                            className={`${isActive(item.href) ? 'text-indigo-500' : 'text-gray-400'
                              } mr-2 h-5 w-5 transition-colors duration-200`}
                          />
                        ) : (
                          <svg
                            className={`${isActive(item.href) ? 'text-indigo-500' : 'text-gray-400'
                              } mr-2 h-5 w-5 transition-colors duration-200`}
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            aria-hidden="true"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.icon} />
                          </svg>
                        )}
                        {item.name}
                        <ChevronDownIcon className={`ml-1 h-4 w-4 transition-transform duration-200 ${newMenuDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {newMenuDropdownOpen && (
                        <div className="absolute left-0 mt-2 w-56 rounded-xl shadow-strong bg-white ring-1 ring-black ring-opacity-5 z-50 border border-gray-100">
                          <div className="py-1" role="menu" aria-orientation="vertical">
                            {item.children.map((subItem) => {
                              if (subItem.requiredPermission && !checkPermission(subItem.requiredPermission)) {
                                return null;
                              }
                              return (
                                <Link
                                  key={subItem.path}
                                  to={`/${subItem.path}`}
                                  className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-all duration-200 hover:shadow-sm"
                                  onClick={() => setNewMenuDropdownOpen(false)}
                                >
                                  {subItem.iconComponent && (
                                    <subItem.iconComponent className="mr-3 h-4 w-4" />
                                  )}
                                  {subItem.name}
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }
                // Special handling for View & Search menu with dropdown
                if (item.name === 'View & Search' && item.children && item.children.length > 0) {
                  return (
                    <div key={item.href} className="relative">
                      <button style={{ height: '100%' }}
                        onClick={() => setViewSearchDropdownOpen(!viewSearchDropdownOpen)}
                        className={`${isActive(item.href)
                            ? 'border-indigo-500 text-gray-900'
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                          } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-all duration-200 hover:shadow-sm`}
                      >
                        {item.iconComponent ? (
                          <item.iconComponent
                            className={`${isActive(item.href) ? 'text-indigo-500' : 'text-gray-400'
                              } mr-2 h-5 w-5 transition-colors duration-200`}
                          />
                        ) : (
                          <svg
                            className={`${isActive(item.href) ? 'text-indigo-500' : 'text-gray-400'
                              } mr-2 h-5 w-5 transition-colors duration-200`}
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            aria-hidden="true"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.icon} />
                          </svg>
                        )}
                        {item.name}
                        <ChevronDownIcon className={`ml-1 h-4 w-4 transition-transform duration-200 ${viewSearchDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {viewSearchDropdownOpen && (
                        <div className="absolute left-0 mt-2 w-56 rounded-xl shadow-strong bg-white ring-1 ring-black ring-opacity-5 z-50 border border-gray-100">
                          <div className="py-1" role="menu" aria-orientation="vertical">
                            {item.children.map((subItem) => {
                              if (subItem.requiredPermission && !checkPermission(subItem.requiredPermission)) {
                                return null;
                              }
                              return (
                                <Link
                                  key={subItem.path}
                                  to={`/${subItem.path}`}
                                  className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-all duration-200 hover:shadow-sm"
                                  onClick={() => setViewSearchDropdownOpen(false)}
                                >
                                  {subItem.iconComponent && (
                                    <subItem.iconComponent className="mr-3 h-4 w-4" />
                                  )}
                                  {subItem.name}
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }
                // Special handling for Admin with dropdown
                if (item.name === 'Admin' && filteredAdminSubmenuItems.length > 0) {
                  return (
                    <div key={item.href} className="relative">
                      <button style={{ height: '100%' }}
                        onClick={() => setAdminDropdownOpen(!adminDropdownOpen)}
                        className={`${isActive(item.href)
                            ? 'border-indigo-500 text-gray-900'
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                          } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-all duration-200 hover:shadow-sm`}
                      >
                        {item.iconComponent ? (
                          <item.iconComponent
                            className={`${isActive(item.href) ? 'text-indigo-500' : 'text-gray-400'
                              } mr-2 h-5 w-5 transition-colors duration-200`}
                          />
                        ) : (
                          <svg
                            className={`${isActive(item.href) ? 'text-indigo-500' : 'text-gray-400'
                              } mr-2 h-5 w-5 transition-colors duration-200`}
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            aria-hidden="true"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.icon} />
                          </svg>
                        )}
                        {item.name}
                        <ChevronDownIcon className={`ml-1 h-4 w-4 transition-transform duration-200 ${adminDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {adminDropdownOpen && (
                        <div className="absolute left-0 mt-2 w-56 rounded-xl shadow-strong bg-white ring-1 ring-black ring-opacity-5 z-50 border border-gray-100">
                          <div className="py-1" role="menu" aria-orientation="vertical">
                            {filteredAdminSubmenuItems.map((subItem) => {
                              // Special handling for Admin Settings with nested dropdown
                              if (subItem.name === 'Admin Settings' && filteredAdminSettingsSubmenuItems.length > 0) {
                                return (
                                  <div key={subItem.path} className="relative">
                                    <button
                                      onClick={() => setAdminSettingsDropdownOpen(!adminSettingsDropdownOpen)}
                                      className={`${location.pathname.startsWith('/admin/settings')
                                          ? 'bg-indigo-50 text-indigo-700'
                                          : 'text-gray-700 hover:bg-gray-50'
                                        } flex items-center justify-between w-full px-4 py-3 text-sm transition-all duration-200 hover:shadow-sm`}
                                    >
                                      <div className="flex items-center">
                                        {subItem.iconComponent && (
                                          <subItem.iconComponent className="mr-3 h-4 w-4" />
                                        )}
                                        {subItem.name}
                                      </div>
                                      <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ${adminSettingsDropdownOpen ? 'rotate-180' : ''}`} />
                                    </button>

                                    {/* Admin Settings Nested Dropdown */}
                                    {adminSettingsDropdownOpen && (
                                      <div className="absolute left-full top-0 ml-1 w-56 rounded-xl shadow-strong bg-white ring-1 ring-black ring-opacity-5 z-50 border border-gray-100">
                                        <div className="py-1">
                                          {filteredAdminSettingsSubmenuItems.map((settingsItem) => (
                                            <Link
                                              key={settingsItem.path}
                                              to={`/admin/settings/${settingsItem.path}`}
                                              className={`${location.pathname === `/admin/settings/${settingsItem.path}`
                                                  ? 'bg-indigo-50 text-indigo-700'
                                                  : 'text-gray-700 hover:bg-gray-50'
                                                } flex items-center px-4 py-3 text-sm transition-all duration-200 hover:shadow-sm`}
                                              onClick={() => {
                                                setAdminDropdownOpen(false);
                                                setAdminSettingsDropdownOpen(false);
                                              }}
                                            >
                                              {settingsItem.name}
                                            </Link>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              }

                              // Regular admin submenu items
                              return (
                                <Link
                                  style={{ paddingBottom: '10px !important' }}
                                  key={subItem.path}
                                  to={`/admin/${subItem.path}`}
                                  className={`${location.pathname === `/admin/${subItem.path}` ||
                                      (subItem.path === 'settings' && location.pathname.startsWith('/admin/settings'))
                                      ? 'bg-indigo-50 text-indigo-700'
                                      : 'text-gray-700 hover:bg-gray-50'
                                    } flex items-center px-4 py-3 text-sm transition-all duration-200 hover:shadow-sm`}
                                  onClick={() => setAdminDropdownOpen(false)}
                                >
                                  {subItem.iconComponent && (
                                    <subItem.iconComponent className="mr-3 h-4 w-4" />
                                  )}
                                  {subItem.name}
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }
                // Regular navigation items
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`${isActive(item.href)
                        ? 'border-indigo-500 text-gray-900'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                      } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-all duration-200 hover:shadow-sm`}
                  >
                    {item.iconComponent ? (
                      <item.iconComponent
                        className={`${isActive(item.href) ? 'text-indigo-500' : 'text-gray-400'
                          } mr-2 h-5 w-5 transition-colors duration-200`}
                      />
                    ) : (
                      <svg
                        className={`${isActive(item.href) ? 'text-indigo-500' : 'text-gray-400'
                          } mr-2 h-5 w-5 transition-colors duration-200`}
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.icon} />
                      </svg>
                    )}
                    {item.name}
                  </Link>
                );
              })}

              {/* Generated Pages Dropdown */}
              {generatedPages.length > 0 && (
                <div className="relative">
                  <button
                    onClick={() => setGeneratedPagesDropdownOpen(!generatedPagesDropdownOpen)}
                    className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-all duration-200 hover:shadow-sm ${generatedPagesDropdownOpen ? 'border-indigo-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}`}
                  >
                    <DocumentTextIcon className="mr-2 h-5 w-5 text-gray-400" />
                    Other Applications
                    <ChevronDownIcon className={`ml-1 h-4 w-4 transition-transform duration-200 ${generatedPagesDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {generatedPagesDropdownOpen && (
                    <div className="absolute left-0 mt-2 w-56 rounded-xl shadow-strong bg-white ring-1 ring-black ring-opacity-5 z-50 border border-gray-100">
                      <div className="py-1" role="menu" aria-orientation="vertical">
                        {generatedPages.map((page) => (
                          <Link
                            key={page.customPageId}
                            to={`/admin/settings/custom-page-submissions/${page.customPageId}`}
                            className={`flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-all duration-200 hover:shadow-sm ${location.pathname === `/admin/settings/custom-page-submissions/${page.customPageId}` ? 'bg-indigo-50 text-indigo-700' : ''}`}
                            onClick={() => setGeneratedPagesDropdownOpen(false)}
                          >
                            {page.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* User Menu and Mobile Menu Button */}
          <div className="flex items-center">
            {/* User Menu */}
            <div className="hidden md:flex md:items-center md:space-x-4">
              <div className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                <div className="h-8 w-8 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 flex items-center justify-center shadow-sm">
                  <span className="text-sm font-medium text-white">
                    {user?.firstName?.[0]}{user?.lastName?.[0]}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{user?.firstName} {user?.lastName}</p>
                  {/* <p className="text-xs text-gray-500">{user?.email}</p> */}
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="btn btn-primary btn-sm"
              >
                Logout
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-lg text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 transition-all duration-200"
              >
                <span className="sr-only">Open main menu</span>
                {isMobileMenuOpen ? (
                  <XMarkIcon className="block h-6 w-6" />
                ) : (
                  <Bars3Icon className="block h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden">
          <div className="pt-2 pb-3 space-y-1 bg-white border-t border-gray-200 shadow-soft">
            {filteredNavigation.map((item) => {
              // Special handling for New menu in mobile menu
              if (item.name === 'New' && item.children && item.children.length > 0) {
                return (
                  <div key={item.href}>
                    <div className="px-3 py-2 text-base font-medium text-gray-500">
                      {item.name}
                    </div>
                    <div className="pl-6 space-y-1">
                      {item.children.map((subItem) => {
                        if (subItem.requiredPermission && !checkPermission(subItem.requiredPermission)) {
                          return null;
                        }
                        return (
                          <Link
                            key={subItem.path}
                            to={`/${subItem.path}`}
                            className={`${location.pathname === `/${subItem.path}`
                                ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                                : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                              } block pl-3 pr-4 py-2 border-l-4 text-sm font-medium transition-all duration-200`}
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <div className="flex items-center">
                              {subItem.iconComponent && (
                                <subItem.iconComponent className="mr-3 h-4 w-4" />
                              )}
                              {subItem.name}
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                );
              }
              // Special handling for View & Search in mobile menu
              if (item.name === 'View & Search' && item.children && item.children.length > 0) {
                return (
                  <div key={item.href}>
                    <div className="px-3 py-2 text-base font-medium text-gray-500">
                      {item.name}
                    </div>
                    <div className="pl-6 space-y-1">
                      {item.children.map((subItem) => {
                        if (subItem.requiredPermission && !checkPermission(subItem.requiredPermission)) {
                          return null;
                        }
                        return (
                          <Link
                            key={subItem.path}
                            to={`/${subItem.path}`}
                            className={`${location.pathname === `/${subItem.path}`
                                ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                                : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                              } block pl-3 pr-4 py-2 border-l-4 text-sm font-medium transition-all duration-200`}
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <div className="flex items-center">
                              {subItem.iconComponent && (
                                <subItem.iconComponent className="mr-3 h-4 w-4" />
                              )}
                              {subItem.name}
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                );
              }
              // Special handling for Admin in mobile menu
              if (item.name === 'Admin' && filteredAdminSubmenuItems.length > 0) {
                return (
                  <div key={item.href}>
                    <div className="px-3 py-2 text-base font-medium text-gray-500">
                      {item.name}
                    </div>
                    <div className="pl-6 space-y-1">
                      {filteredAdminSubmenuItems.map((subItem) => {
                        // Special handling for Admin Settings in mobile menu
                        if (subItem.name === 'Admin Settings' && filteredAdminSettingsSubmenuItems.length > 0) {
                          return (
                            <div key={subItem.path}>
                              <div className="px-3 py-2 text-sm font-medium text-gray-400">
                                {subItem.name}
                              </div>
                              <div className="pl-6 space-y-1">
                                {filteredAdminSettingsSubmenuItems.map((settingsItem) => (
                                  <Link
                                    key={settingsItem.path}
                                    to={`/admin/settings/${settingsItem.path}`}
                                    className={`${location.pathname === `/admin/settings/${settingsItem.path}`
                                        ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                                        : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                                      } block pl-3 pr-4 py-2 border-l-4 text-sm font-medium transition-all duration-200`}
                                    onClick={() => setIsMobileMenuOpen(false)}
                                  >
                                    {settingsItem.name}
                                  </Link>
                                ))}
                              </div>
                            </div>
                          );
                        }

                        // Regular admin submenu items
                        return (
                          <Link
                            key={subItem.path}
                            to={`/admin/${subItem.path}`}
                            className={`${location.pathname === `/admin/${subItem.path}` ||
                                (subItem.path === 'settings' && location.pathname.startsWith('/admin/settings'))
                                ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                                : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                              } block pl-3 pr-4 py-2 border-l-4 text-sm font-medium transition-all duration-200`}
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <div className="flex items-center">
                              {subItem.iconComponent && (
                                <subItem.iconComponent className="mr-3 h-4 w-4" />
                              )}
                              {subItem.name}
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              // Regular mobile navigation items
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`${isActive(item.href)
                      ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                      : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                    } block pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-all duration-200`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <div className="flex items-center">
                    {item.iconComponent ? (
                      <item.iconComponent
                        className={`${isActive(item.href) ? 'text-indigo-500' : 'text-gray-400'
                          } mr-3 h-6 w-6 transition-colors duration-200`}
                      />
                    ) : (
                      <svg
                        className={`${isActive(item.href) ? 'text-indigo-500' : 'text-gray-400'
                          } mr-3 h-6 w-6 transition-colors duration-200`}
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.icon} />
                      </svg>
                    )}
                    {item.name}
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Mobile user menu */}
          <div className="pt-4 pb-3 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center px-4">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 flex items-center justify-center shadow-sm">
                  <span className="text-sm font-medium text-white">
                    {user?.firstName?.[0]}{user?.lastName?.[0]}
                  </span>
                </div>
              </div>
              <div className="ml-3">
                <div className="text-base font-medium text-gray-800">{user?.firstName} {user?.lastName}</div>
                {/* <div className="text-sm font-medium text-gray-500">{user?.email}</div> */}
              </div> 
            </div>
            <div className="mt-3 space-y-1">
              <button
                onClick={handleLogout}
                className="block w-full text-left px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-all duration-200"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close dropdowns */}
      {(adminDropdownOpen || adminSettingsDropdownOpen || newMenuDropdownOpen || viewSearchDropdownOpen || generatedPagesDropdownOpen) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setAdminDropdownOpen(false);
            setAdminSettingsDropdownOpen(false);
            setNewMenuDropdownOpen(false);
            setViewSearchDropdownOpen(false);
            setGeneratedPagesDropdownOpen(false);
          }}
        />
      )}
    </nav>
  );
};

export default TopNavigation; 